import { getAuthToken } from './authService';

export type TicketCategory = 'otp' | 'donation' | 'mosque' | 'other';
export interface TicketSubmission {
  requestId: string;
  category: TicketCategory;
  subject: string;
  description: string;
  contactEmail: string | null;
  contactPhone: string | null;
  language: 'ar' | 'en';
}
export interface TicketReceipt { ticketReference: string; status: string; createdAt: string }
export class TicketApiError extends Error {
  constructor(public status: number, public retryAfter = 0) { super('Ticket request failed'); }
}

// Read the existing login only when the visitor submits a ticket. Never change or decode that login here.
export const readTicketLogin = () => getAuthToken();

export async function createSupportTicket(baseUrl: string, submission: TicketSubmission,
  loginToken: string | null, signal: AbortSignal): Promise<TicketReceipt> {
  const url = new URL(baseUrl);
  const local = url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  if (url.username || url.password || url.search || url.hash || url.pathname !== '/' ||
      (url.protocol !== 'https:' && !(process.env.NODE_ENV === 'development' && local))) throw new TicketApiError(0);

  // Only this explicit ticket submission uses the website JWT. Guided support keeps using its own token.
  // A guest sends no Authorization header; the API determines user_id after validating a supplied JWT.
  const response = await fetch(`${url.origin}/api/support/tickets`, {
    method: 'POST', credentials: 'omit', cache: 'no-store', redirect: 'error', signal,
    headers: { 'Content-Type': 'application/json', ...(loginToken ? { Authorization: `Bearer ${loginToken}` } : {}) },
    body: JSON.stringify(submission),
  });
  if (!response.ok) {
    const retry = response.headers.get('Retry-After');
    const seconds = retry && /^\d+$/.test(retry) ? Number(retry) : response.status === 429 ? 60 : 30;
    // Do not display server exceptions, SQL details or arbitrary response text inside the chat.
    throw new TicketApiError(response.status, Math.max(1, Math.min(300, seconds)));
  }
  const receipt = await response.json();
  if (!receipt || typeof receipt.ticketReference !== 'string' ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(receipt.ticketReference) ||
      !['open', 'in_progress', 'resolved', 'closed'].includes(receipt.status) ||
      typeof receipt.createdAt !== 'string' || !Number.isFinite(Date.parse(receipt.createdAt))) throw new TicketApiError(0);
  return receipt;
}
